<template>
    <div class="container-press">
        <div class="press-inner">
            <!-- Header -->
            <div class="press-header">
                <div class="press-title-block">
                    <h2 class="press-title"><router-link :to="{ name: $t('newsRouteName'), params: { id: getNewsId } }">
                            {{ $t("mediaAboutFIA") }}
                        </router-link></h2>
                    <p class="press-subtitle">{{ $t('mediaAboutFIASubititle') }}</p>
                </div>
                <!-- <a href="#" class="press-view-all">Pogledaj sve <i class="fa fa-arrow-right"></i></a> -->
            </div>

            <!-- Carousel -->
            <div class="press-carousel-wrapper">
                <button class="press-nav-btn prev-btn" @click="scrollCarousel(-1)" :disabled="atStart">
                    <i class="fa fa-chevron-left"></i>
                </button>

                <div class="press-carousel-view" ref="carouselView">
                    <div class="press-item-list" ref="itemList">
                        <div v-for="item in displayedPressItems" :key="item.id" class="press-card">
                            <!-- Source logo / name -->
                            <div class="press-card-top">
                                <span class="press-source">{{ item.subtitle }}</span>
                                <span class="press-date">{{ item.dateTime | moment($t("dateFormat")) }}</span>
                            </div>

                            <!-- Title -->
                            <h4 class="press-card-title">{{ item.title }}</h4>

                            <!-- Tags + external link -->
                            <div class="press-card-bottom">
                                <div class="press-tags">
                                    <span v-for="tag in parseTags(item.tags)" :key="tag.tagId" class="press-tag">{{
                                        tag.name }}</span>
                                </div>
                                <a :href="item.url" target="_blank" class="press-external-link" title="Otvori izvor">
                                    <i class="fa fa-external-link"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <button class="press-nav-btn next-btn" @click="scrollCarousel(1)" :disabled="atEnd">
                    <i class="fa fa-chevron-right"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

const PRESS_CLIPPING_TAG_ID = 1071

export default {
    name: "PressClippingCarousel",
    props: {
        limit: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            atStart: true,
            atEnd: false,
        }
    },
    computed: {
        ...mapGetters(['getAllPressClippings']),
        displayedPressItems() {
            const items = this.getAllPressClippings
            return this.limit ? items.slice(0, this.limit) : items
        }
    },
    methods: {
        parseTags(tagsJson) {
            if (!tagsJson) return []
            try {
                return JSON.parse(tagsJson).filter(t => t.tagId !== PRESS_CLIPPING_TAG_ID)
            } catch {
                return []
            }
        },
        scrollCarousel(direction) {
            const list = this.$refs.itemList;
            const card = list.querySelector(".press-card");
            if (!card) return;
            const cardWidth = card.getBoundingClientRect().width + 16;
            list.scrollLeft += direction * cardWidth;
            this.$nextTick(() => { this.updateNavState(); });
        },
        updateNavState() {
            const list = this.$refs.itemList;
            if (!list) return;
            this.atStart = list.scrollLeft <= 0;
            this.atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
        },
    },
    mounted() {
        const list = this.$refs.itemList;
        if (list) {
            list.addEventListener("scroll", this.updateNavState);
            this.updateNavState();
        }
    },
    beforeDestroy() {
        const list = this.$refs.itemList;
        if (list) list.removeEventListener("scroll", this.updateNavState);
    },
}
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────── */
.container-press {
    width: 100%;
    padding-top: 2.5rem;
}

.press-inner {
    max-width: 1440px;
    margin: 0 auto;
}

/* ── Header ─────────────────────────────────────────────── */
.press-title-block {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
}

.press-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 1.5rem;
}

.press-title a{
    font-size: 30px;
    font-weight: 600;
    color: var(--main-color, #1a2e5a);
    margin: 0 0 2px;
    text-decoration: none;
}

.press-subtitle {
    font-size: 13px;
    color: #757575;
    margin: 0;
}

.press-view-all {
    font-size: 14px;
    font-weight: 600;
    color: var(--main-color, #1a2e5a);
    text-decoration: none;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: opacity 0.2s;
}

.press-view-all:hover {
    opacity: 0.7;
}

/* ── Carousel wrapper ───────────────────────────────────── */
.press-carousel-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    width: 100%;
}

.press-carousel-view {
    flex: 1;
    overflow: hidden;
}

.press-item-list {
    display: flex;
    gap: 16px;
    scroll-behavior: smooth;
    overflow: hidden;
    /* hide scrollbar, JS-driven */
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.press-item-list::-webkit-scrollbar {
    display: none;
}

/* ── Cards ─────────────────────────────────────────────── */
.press-card {
    flex-shrink: 0;
    width: calc((100% - 64px) / 5);
    /* 5 cards visible on wide screens */
    min-width: 200px;
    background-color: var(--light-color, #ffffff);
    border-radius: 10px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid rgba(0, 0, 0, 0.07);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.press-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

/* Top row: source name + date */
.press-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.press-source {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.3px;
    line-height: 1;
    color: var(--main-color);
}

.press-date {
    font-size: 12px;
    color: #9ca3af;
    white-space: nowrap;
}

/* Title */
.press-card-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
    margin: 0;
    /* clamp to 3 lines */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
}

/* Bottom row: tags + link */
.press-card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
    gap: 8px;
}

.press-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    flex: 1;
    overflow: hidden;
}

.press-tag {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
    color: #6b7280;
    background-color: #f3f4f6;
    border-radius: 4px;
    padding: 3px 7px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.press-external-link {
    color: #9ca3af;
    font-size: 13px;
    flex-shrink: 0;
    text-decoration: none;
    transition: color 0.2s;
}

.press-external-link:hover {
    color: var(--main-color, #1a2e5a);
}

/* ── Nav buttons ────────────────────────────────────────── */
.press-nav-btn {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 50%;
    background-color: var(--secondary-color);
    border: none;
    color: var(--main-color, #1a2e5a);
    font-size: 22px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    padding: 0;
}

.press-nav-btn:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.press-nav-btn:disabled {
    opacity: 0.3;
    cursor: default;
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 1200px) {
    .press-card {
        width: calc((100% - 48px) / 4);
    }
}

@media (max-width: 900px) {
    .press-card {
        width: calc((100% - 32px) / 3);
    }
}

@media (max-width: 640px) {
    .press-card {
        width: calc((100% - 16px) / 2);
    }
}

@media (max-width: 420px) {
    .press-card {
        width: 85%;
        min-width: 85%;
    }
}
</style>